import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Library, Upload } from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFeatureFlags } from '@/lib/feature-flags';
import { Ratio } from '@/models/zoneplacement';
import type { LibraryCreative } from '../AssetLibrary/hooks';
import { AssetLibrary } from '../AssetLibrary';
import { AssetDisplay } from '../AssetDisplay';

interface Props {
  disabled: boolean;
  handleDelete: () => void;
  ratio: Ratio | undefined;
  readOnly: boolean;
  setAssetFromLibrary: (id: number, duration: number) => void;
  showUploadPreview: boolean;
  uploadAsset: ReactNode;
  uploadPreview: ReactNode;
}

export function AssetMethod({
  disabled,
  handleDelete,
  ratio,
  readOnly,
  setAssetFromLibrary,
  showUploadPreview,
  uploadAsset,
  uploadPreview,
}: Props) {
  const { assetLibrary } = useFeatureFlags();
  const [selected, setSelected] = useState<LibraryCreative | null>(null);
  const isFirstRender = useRef(true);

  function setAsset(asset: LibraryCreative) {
    setSelected(asset);
    setAssetFromLibrary(asset.id, asset.duration_ms / 1_000);
  }

  function removeAsset() {
    setSelected(null);
    handleDelete();
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (selected !== null) {
      removeAsset();
    } else if (showUploadPreview) {
      handleDelete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratio]);

  useEffect(() => {
    if (showUploadPreview) {
      setSelected(null);
    }
  }, [showUploadPreview]);

  return assetLibrary ? (
    <Tabs defaultValue="upload">
      <TabsList className="mb-1" variant="line">
        <TabsTrigger disabled={disabled || readOnly} value="upload">
          <Upload />
          Upload a file
        </TabsTrigger>
        <TabsTrigger disabled={disabled || readOnly} value="library">
          <Library />
          Choose from library
        </TabsTrigger>
      </TabsList>
      <TabsContent value="upload">
        {showUploadPreview ? uploadPreview : uploadAsset}
      </TabsContent>
      <TabsContent value="library">
        {selected ? (
          <AssetDisplay
            asset={selected}
            readOnly={readOnly}
            removeAsset={removeAsset}
          />
        ) : (
          <AssetLibrary ratio={ratio} setAsset={setAsset} />
        )}
      </TabsContent>
    </Tabs>
  ) : showUploadPreview ? (
    uploadPreview
  ) : (
    uploadAsset
  );
}
